/**
 * Daily Logs Page
 * BuilderBel Construction Project Management System
 */

import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileEdit,
  Inbox,
  List,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { DailyLogCalendar, DailyLogDrawer } from '../components/dailyLogs';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useDeleteDailyLogMutation,
  useGetDailyLogStatsQuery,
  useGetDailyLogsQuery,
} from '../services/dailyLogApi';
import type { DailyLog, DailyLogStatus } from '../types/dailyLog';

// Status filter options
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

// Sort options - matches backend DailyLogListParams
const SORT_OPTIONS = [
  { value: 'logDate', label: 'Log Date' },
  { value: 'createdAt', label: 'Created Date' },
  { value: 'dayNumber', label: 'Day Number' },
  { value: 'status', label: 'Status' },
  { value: 'totalWorkers', label: 'Workers' },
  { value: 'totalHours', label: 'Hours' },
];

// View mode type
type ViewMode = 'list' | 'calendar';

// Get status variant for StatusBadge
const getStatusVariant = (
  status: DailyLogStatus
): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'submitted':
      return 'info';
    case 'rejected':
      return 'error';
    case 'draft':
    default:
      return 'neutral';
  }
};

// Format date
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const DailyLogs: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // State for view mode
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // State for filters and pagination
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<DailyLogStatus | ''>('');
  const [sortBy, setSortBy] = useState('logDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const limit = 20;

  // State for drawer
  const [selectedLog, setSelectedLog] = useState<DailyLog | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');
  const [createDate, setCreateDate] = useState<string | undefined>();

  // State for dropdown menu
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // API calls
  const {
    data: logsResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetDailyLogsQuery(
    {
      projectId: projectId || '',
      params: {
        page,
        limit,
        search: search || undefined,
        status: status || undefined,
        sortBy: sortBy as
          | 'logDate'
          | 'dayNumber'
          | 'status'
          | 'createdAt'
          | 'totalWorkers'
          | 'totalHours',
        sortOrder,
      },
    },
    { skip: !projectId }
  );

  const { data: statsResponse } = useGetDailyLogStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const [deleteDailyLog, { isLoading: isDeleting }] = useDeleteDailyLogMutation();

  // Extract data
  const dailyLogs = useMemo(() => logsResponse?.data?.dailyLogs || [], [logsResponse]);
  const pagination = logsResponse?.data?.pagination;
  const stats = statsResponse?.data;

  // Computed stats for header
  const headerStats = useMemo(() => {
    if (!stats) return [];
    return [
      {
        title: 'Total Logs',
        value: stats.total || 0,
        variant: 'brand' as const,
      },
      {
        title: 'Approved',
        value: stats.approved || 0,
        variant: 'success' as const,
      },
      {
        title: 'Pending',
        value: stats.submitted || 0,
        variant: 'warning' as const,
      },
      {
        title: 'With Delays',
        value: stats.daysWithDelays || 0,
        variant: 'danger' as const,
      },
    ];
  }, [stats]);

  // Handlers
  const handleCreateLog = useCallback((date?: string) => {
    setSelectedLog(null);
    setCreateDate(date);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleViewLog = useCallback((log: DailyLog) => {
    setSelectedLog(log);
    setCreateDate(undefined);
    setDrawerMode('view');
    setIsDrawerOpen(true);
  }, []);

  const handleEditLog = useCallback((log: DailyLog) => {
    setSelectedLog(log);
    setCreateDate(undefined);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedLog(null);
    setCreateDate(undefined);
  }, []);

  const handleDeleteLog = useCallback(
    async (log: DailyLog) => {
      if (!projectId) return;
      if (window.confirm(`Are you sure you want to delete ${log.logNumber}?`)) {
        try {
          await deleteDailyLog({
            projectId,
            logId: log._id,
          }).unwrap();
          toast.success('Daily log deleted successfully');
        } catch (error) {
          console.error('Failed to delete daily log:', error);
          toast.error('Failed to delete daily log');
        }
      }
    },
    [projectId, deleteDailyLog, toast]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle PDF Download
  const handleDownloadPDF = useCallback(
    async (log: DailyLog) => {
      if (!projectId) return;
      try {
        const response = await fetch(`/api/projects/${projectId}/daily-logs/${log._id}/pdf`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          },
        });
        if (!response.ok) throw new Error('Failed to download PDF');
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${log.logNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded successfully');
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  // Handle calendar date selection
  const handleSelectDate = useCallback(
    (date: string, logId?: string) => {
      if (logId) {
        const log = dailyLogs.find(l => l._id === logId);
        if (log) {
          handleViewLog(log);
        }
      }
    },
    [dailyLogs, handleViewLog]
  );

  // Render view toggle
  const renderViewToggle = () => (
    <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
      <button
        type="button"
        onClick={() => setViewMode('list')}
        className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
          viewMode === 'list'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <List className="h-3.5 w-3.5" />
        <span>List</span>
      </button>
      <button
        type="button"
        onClick={() => setViewMode('calendar')}
        className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
          viewMode === 'calendar'
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <CalendarDays className="h-3.5 w-3.5" />
        <span>Calendar</span>
      </button>
    </div>
  );

  // Render filters
  const renderFilters = () => (
    <div className="flex items-center gap-2">
      {renderViewToggle()}
      {viewMode === 'list' && (
        <SearchableDropdown
          placeholder="Status"
          value={status}
          onChange={value => {
            setStatus(value as DailyLogStatus | '');
            setPage(1);
          }}
          options={STATUS_OPTIONS}
          searchable={false}
        />
      )}
    </div>
  );

  // Render pagination
  const renderPagination = () => {
    if (!pagination || pagination.pages <= 1 || viewMode === 'calendar') return null;

    const { page: currentPage, pages: totalPages, total } = pagination;
    const startItem = (currentPage - 1) * limit + 1;
    const endItem = Math.min(currentPage * limit, total);

    return (
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Showing {startItem}-{endItem} of {total} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // Render row actions dropdown
  const renderRowActions = (log: DailyLog) => {
    const isMenuOpen = activeMenuId === log._id;

    const menuItems: (ActionMenuItem | 'divider')[] = [
      {
        label: 'View Details',
        icon: <Eye className="h-3.5 w-3.5" />,
        onClick: () => handleViewLog(log),
      },
      {
        label: 'Edit',
        icon: <Pencil className="h-3.5 w-3.5" />,
        onClick: () => handleEditLog(log),
        hidden: log.status !== 'draft' && log.status !== 'rejected',
      },
      'divider',
      {
        label: 'Download PDF',
        icon: <Download className="h-3.5 w-3.5" />,
        onClick: () => handleDownloadPDF(log),
      },
      'divider',
      {
        label: 'Delete',
        icon: <Trash2 className="h-3.5 w-3.5" />,
        onClick: () => handleDeleteLog(log),
        variant: 'danger',
        disabled: isDeleting,
        hidden: log.status !== 'draft',
      },
    ];

    return (
      <ActionMenu
        id={log._id}
        items={menuItems}
        isOpen={isMenuOpen}
        onOpenChange={open => setActiveMenuId(open ? log._id : null)}
      />
    );
  };

  // Show loading state
  if (isLoading && dailyLogs.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // Show project selection required
  if (!projectId) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <EmptyState
          icon={Inbox}
          title="No Project Selected"
          description="Please select a project to view daily logs."
        />
      </div>
    );
  }

  return (
    <ModuleLayout
      title="Daily Logs"
      description="Document daily site activities and progress"
      icon={<FileEdit className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'New Log',
        onClick: () => handleCreateLog(),
        icon: <Plus className="h-3 w-3 mr-1" />,
      }}
      stats={headerStats}
      searchValue={viewMode === 'list' ? search : ''}
      onSearchChange={
        viewMode === 'list'
          ? value => {
              setSearch(value);
              setPage(1);
            }
          : undefined
      }
      filters={renderFilters()}
      sortOptions={viewMode === 'list' ? SORT_OPTIONS : undefined}
      sortValue={viewMode === 'list' ? sortBy : undefined}
      onSortChange={viewMode === 'list' ? setSortBy : undefined}
      sortOrder={viewMode === 'list' ? sortOrder : undefined}
      onSortOrderChange={viewMode === 'list' ? setSortOrder : undefined}
      onRefresh={refetch}
      isLoading={isFetching}
      pagination={renderPagination()}
    >
      {viewMode === 'calendar' ? (
        <div className="h-[calc(100vh-220px)]">
          <DailyLogCalendar onSelectDate={handleSelectDate} onCreateLog={handleCreateLog} />
        </div>
      ) : dailyLogs.length === 0 ? (
        <EmptyState
          icon={FileEdit}
          title="No daily logs found"
          description={
            search || status
              ? "Try adjusting your filters to find what you're looking for."
              : 'Document daily site activities, worker attendance, weather conditions, and progress updates.'
          }
          action={
            !search && !status
              ? {
                  label: 'Create Daily Log',
                  onClick: () => handleCreateLog(),
                }
              : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Log #
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Day
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Weather
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Workers
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hours
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {dailyLogs.map(log => (
                <tr
                  key={log._id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewLog(log)}
                >
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">{log.logNumber}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900">{formatDate(log.logDate)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">Day {log.dayNumber}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">
                        {log.weather?.conditions || '-'}
                      </span>
                      {log.weather?.temperatureHigh && (
                        <span className="text-xs text-gray-400">
                          {log.weather.temperatureHigh}°F
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">{log.totalWorkers}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{log.totalHours?.toFixed(1) || 0}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={log.status}
                      variant={getStatusVariant(log.status)}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {renderRowActions(log)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Daily Log Drawer */}
      <DailyLogDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        dailyLog={selectedLog}
        mode={drawerMode}
        onSuccess={refetch}
        defaultDate={createDate}
      />
    </ModuleLayout>
  );
};

export default DailyLogs;
